import React, { useState, useEffect } from 'react';
import Navigation from '@/components/shared/Navigation';
import LoadingSpinner from '@/components/shared/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from '@/components/ui/use-toast';
import { 
  Settings, 
  MessageSquare, 
  Mail, 
  Save, 
  TestTube,
  RefreshCw,
  Shield,
  Database,
  Globe,
  Phone
} from 'lucide-react';
import { settingsApi, environmentApi } from '@/lib/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';

interface SystemSettings {
  system_name: string;
  welcome_sms_template: string;
  payment_reminder_template: string;
  renewal_reminder_template: string;
  auto_renew_enabled: boolean;
  payment_grace_period: number;
  membership_fee_currency: string;
  sms_sender_name: string;
  email_from_address: string;
  email_from_name: string;
}

interface SMSConfig {
  sms_base_url: string;
  sms_identifier: string;
  sms_password: string;
  sms_enabled: boolean;
  enabled?: boolean;
}

interface EmailTemplate {
  name: string;
  subject: string;
  body: string;
  variables: string[];
}

const AdminSettings: React.FC = () => {
  const [settings, setSettings] = useState<SystemSettings>({
    system_name: 'OG Basketball Club',
    welcome_sms_template: 'Welcome to {{system_name}}! Your membership is now active.',
    payment_reminder_template: 'Dear {{member_name}}, your payment of {{amount}} TZS is due on {{due_date}}.',
    renewal_reminder_template: 'Your membership expires on {{expiry_date}}. Please renew to continue enjoying our services.',
    auto_renew_enabled: true,
    payment_grace_period: 7,
    membership_fee_currency: 'TZS',
    sms_sender_name: 'OG Basketball',
    email_from_address: 'noreply@ogbasketball.com',
    email_from_name: 'OG Basketball Club',
  });

  const [smsConfig, setSmsConfig] = useState<SMSConfig>({
    sms_base_url: '',
    sms_identifier: '',
    sms_password: '',
    sms_enabled: false,
  });

  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [testSMSPhone, setTestSMSPhone] = useState('');
  const [testSMSMessage, setTestSMSMessage] = useState('Test SMS from OG Basketball Club system');
  const [isTesting, setIsTesting] = useState(false);

  useEffect(() => {
    fetchAllSettings();
  }, []);

  const fetchAllSettings = async () => {
    try {
      setIsLoading(true);
      
      // Fetch system settings, email templates, and SMS config in parallel
      const [systemData, templatesData, smsConfigData] = await Promise.all([
        settingsApi.getAll(),
        settingsApi.getEmailTemplates(),
        environmentApi.getSMSConfig().catch(() => ({
          sms_base_url: '',
          sms_identifier: '',
          sms_password: '',
          sms_enabled: false
        }))
      ]);
      
      setSettings(prev => ({ ...prev, ...(systemData as Partial<SystemSettings>) }));
      setEmailTemplates((templatesData as EmailTemplate[]) || []);
      setSmsConfig(prev => ({ ...prev, ...(smsConfigData as Partial<SMSConfig>) }));
      
    } catch (error: any) {
      console.error('Error fetching settings:', error);
      toast({
        title: "Error",
        description: "Failed to load settings",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setIsSaving(true);
      
      // Save both system settings and SMS configuration
      await Promise.all([
        settingsApi.update(settings),
        environmentApi.updateSMSConfig(smsConfig).catch((error) => {
          console.warn('SMS config update failed:', error);
          // Don't fail the entire save if SMS config fails
        })
      ]);
      
      toast({
        title: "Success",
        description: "System settings saved successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to save settings",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestSMS = async () => {
    if (!testSMSPhone.trim() || !testSMSMessage.trim()) {
      toast({
        title: "Error",
        description: "Please enter both phone number and message",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsTesting(true);
      
      // Try the environment SMS test first, then fallback to settings
      try {
        await environmentApi.testSMSConfig(testSMSPhone, testSMSMessage);
      } catch (envError) {
        console.warn('Environment SMS test failed, trying settings API:', envError);
        await settingsApi.testSMS(testSMSPhone, testSMSMessage);
      }
      
      toast({
        title: "Success",
        description: "Test SMS sent successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to send test SMS",
        variant: "destructive",
      });
    } finally {
      setIsTesting(false);
    }
  };

  const handleUpdateEmailTemplate = async (templateName: string, subject: string, body: string) => {
    if (!templateName || !subject || !body) {
      toast({
        title: "Error",
        description: "Template name, subject, and body are required",
        variant: "destructive",
      });
      return;
    }

    try {
      await settingsApi.updateEmailTemplate({
        template_name: templateName,
        subject: subject.trim(),
        body: body.trim()
      });
      
      toast({
        title: "Success",
        description: `${templateName} template updated successfully`,
      });
      
      // Refresh templates
      try {
        const templatesData = await settingsApi.getEmailTemplates();
        setEmailTemplates((templatesData as EmailTemplate[]) || []);
      } catch (refreshError) {
        console.warn('Failed to refresh templates after update:', refreshError);
      }
      
    } catch (error: any) {
      console.error('Email template update error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update email template",
        variant: "destructive",
      });
    }
  };

  const handleSaveSMSSettings = async () => {
    try {
      setIsSaving(true);
      
      // Save SMS-related system settings and SMS configuration
      await Promise.all([
        settingsApi.update({
          sms_sender_name: settings.sms_sender_name,
          welcome_sms_template: settings.welcome_sms_template,
          payment_reminder_template: settings.payment_reminder_template,
          renewal_reminder_template: settings.renewal_reminder_template
        }),
        environmentApi.updateSMSConfig(smsConfig)
      ]);
      
      toast({
        title: "Success",
        description: "SMS settings saved successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to save SMS settings",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="flex items-center justify-center min-h-[50vh]">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">System Settings</h1>
          <p className="text-gray-600">Configure system-wide settings and preferences</p>
        </div>

        <Tabs defaultValue="general" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="general" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              General
            </TabsTrigger>
            <TabsTrigger value="sms" className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              SMS
            </TabsTrigger>
            <TabsTrigger value="email" className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Email
            </TabsTrigger>
            <TabsTrigger value="security" className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Security
            </TabsTrigger>
          </TabsList>

          {/* General Settings */}
          <TabsContent value="general">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>System Information</CardTitle>
                  <CardDescription>
                    Basic system configuration and branding
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="system_name">System Name</Label>
                      <Input
                        id="system_name"
                        value={settings.system_name}
                        onChange={(e) => setSettings(prev => ({ ...prev, system_name: e.target.value }))}
                        placeholder="OG Basketball Club"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="currency">Currency</Label>
                      <Input
                        id="currency"
                        value={settings.membership_fee_currency}
                        onChange={(e) => setSettings(prev => ({ ...prev, membership_fee_currency: e.target.value }))}
                        placeholder="TZS"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="email_from_name">Email From Name</Label>
                      <Input
                        id="email_from_name"
                        value={settings.email_from_name}
                        onChange={(e) => setSettings(prev => ({ ...prev, email_from_name: e.target.value }))}
                        placeholder="OG Basketball Club"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="email_from_address">Email From Address</Label>
                      <Input
                        id="email_from_address"
                        type="email"
                        value={settings.email_from_address}
                        onChange={(e) => setSettings(prev => ({ ...prev, email_from_address: e.target.value }))}
                        placeholder="noreply@ogbasketball.com"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Membership Settings</CardTitle>
                  <CardDescription>
                    Configure membership and payment settings
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="text-base font-medium">Auto-Renewal</Label>
                      <p className="text-sm text-gray-500">
                        Automatically renew memberships when payments are received
                      </p>
                    </div>
                    <Switch
                      checked={settings.auto_renew_enabled}
                      onCheckedChange={(checked) => setSettings(prev => ({ ...prev, auto_renew_enabled: checked }))}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="grace_period">Payment Grace Period (days)</Label>
                    <Input
                      id="grace_period"
                      type="number"
                      value={settings.payment_grace_period}
                      onChange={(e) => setSettings(prev => ({ ...prev, payment_grace_period: parseInt(e.target.value) || 0 }))}
                      placeholder="7"
                    />
                    <p className="text-sm text-gray-500">
                      Number of days after due date before membership is suspended
                    </p>
                  </div>
                </CardContent>
              </Card>

              <div className="flex justify-end">
                <Button onClick={handleSaveSettings} disabled={isSaving}>
                  {isSaving ? <LoadingSpinner size="sm" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Settings
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* SMS Settings */}
          <TabsContent value="sms">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>SMS Configuration</CardTitle>
                  <CardDescription>
                    Configure SMS service provider settings
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="text-base font-medium">Enable SMS</Label>
                      <p className="text-sm text-gray-500">
                        Enable or disable SMS functionality system-wide
                      </p>
                    </div>
                    <Switch
                      checked={smsConfig.sms_enabled}
                      onCheckedChange={(checked) => setSmsConfig(prev => ({ ...prev, sms_enabled: checked }))}
                    />
                  </div>

                  <Separator />

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="sms_sender">SMS Sender Name</Label>
                      <Input
                        id="sms_sender"
                        value={settings.sms_sender_name}
                        onChange={(e) => setSettings(prev => ({ ...prev, sms_sender_name: e.target.value }))}
                        placeholder="OG Basketball"
                        maxLength={11}
                      />
                      <p className="text-sm text-gray-500">Maximum 11 characters</p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="sms_base_url">SMS API Base URL</Label>
                      <Input
                        id="sms_base_url"
                        value={smsConfig.sms_base_url}
                        onChange={(e) => setSmsConfig(prev => ({ ...prev, sms_base_url: e.target.value }))}
                        placeholder="https://sms.provider.com/api"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="sms_identifier">SMS Identifier</Label>
                        <Input
                          id="sms_identifier"
                          value={smsConfig.sms_identifier}
                          onChange={(e) => setSmsConfig(prev => ({ ...prev, sms_identifier: e.target.value }))}
                          placeholder="Your SMS identifier"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="sms_password">SMS Password</Label>
                        <Input
                          id="sms_password"
                          type="password"
                          value={smsConfig.sms_password}
                          onChange={(e) => setSmsConfig(prev => ({ ...prev, sms_password: e.target.value }))}
                          placeholder="Your SMS password"
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>SMS Templates</CardTitle>
                  <CardDescription>
                    Configure SMS message templates
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="welcome_sms">Welcome SMS Template</Label>
                    <Textarea
                      id="welcome_sms"
                      value={settings.welcome_sms_template}
                      onChange={(e) => setSettings(prev => ({ ...prev, welcome_sms_template: e.target.value }))}
                      placeholder="Welcome to {{system_name}}!"
                      rows={3}
                    />
                    <p className="text-sm text-gray-500">
                      Available variables: {`{{system_name}}`}, {`{{member_name}}`}, {`{{membership_type}}`}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="payment_reminder">Payment Reminder Template</Label>
                    <Textarea
                      id="payment_reminder"
                      value={settings.payment_reminder_template}
                      onChange={(e) => setSettings(prev => ({ ...prev, payment_reminder_template: e.target.value }))}
                      placeholder="Payment reminder message..."
                      rows={3}
                    />
                    <p className="text-sm text-gray-500">
                      Available variables: {`{{member_name}}`}, {`{{amount}}`}, {`{{due_date}}`}, {`{{system_name}}`}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="renewal_reminder">Renewal Reminder Template</Label>
                    <Textarea
                      id="renewal_reminder"
                      value={settings.renewal_reminder_template}
                      onChange={(e) => setSettings(prev => ({ ...prev, renewal_reminder_template: e.target.value }))}
                      placeholder="Renewal reminder message..."
                      rows={3}
                    />
                    <p className="text-sm text-gray-500">
                      Available variables: {`{{member_name}}`}, {`{{expiry_date}}`}, {`{{system_name}}`}
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Test SMS</CardTitle>
                  <CardDescription>
                    Send a test SMS to verify configuration
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="test_phone">Phone Number</Label>
                      <Input
                        id="test_phone"
                        value={testSMSPhone}
                        onChange={(e) => setTestSMSPhone(e.target.value)}
                        placeholder="+255621728109"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="test_message">Test Message</Label>
                      <Input
                        id="test_message"
                        value={testSMSMessage}
                        onChange={(e) => setTestSMSMessage(e.target.value)}
                        placeholder="Test message"
                      />
                    </div>
                  </div>
                  
                  <Button onClick={handleTestSMS} disabled={isTesting} variant="outline">
                    {isTesting ? <LoadingSpinner size="sm" /> : <TestTube className="w-4 h-4 mr-2" />}
                    Send Test SMS
                  </Button>
                </CardContent>
              </Card>

              <div className="flex justify-end">
                <Button onClick={handleSaveSMSSettings} disabled={isSaving}>
                  {isSaving ? <LoadingSpinner size="sm" /> : <Save className="w-4 h-4 mr-2" />}
                  Save SMS Settings
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Email Settings */}
          <TabsContent value="email">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Email Templates</CardTitle>
                  <CardDescription>
                    Manage email templates for automated communications
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {emailTemplates.length > 0 ? (
                    <div className="space-y-6">
                      {emailTemplates.map((template, index) => (
                        <div key={template.name} className="border rounded-lg p-4">
                          <h4 className="font-medium mb-3 capitalize">{template.name} Template</h4>
                          <div className="space-y-3">
                            <div className="space-y-2">
                              <Label>Subject</Label>
                              <Input
                                value={template.subject}
                                onChange={(e) => {
                                  const updated = [...emailTemplates];
                                  updated[index].subject = e.target.value;
                                  setEmailTemplates(updated);
                                }}
                                placeholder="Email subject"
                              />
                            </div>
                            
                            <div className="space-y-2">
                              <Label>Body</Label>
                              <Textarea
                                value={template.body}
                                onChange={(e) => {
                                  const updated = [...emailTemplates];
                                  updated[index].body = e.target.value;
                                  setEmailTemplates(updated);
                                }}
                                placeholder="Email body"
                                rows={5}
                              />
                            </div>
                            
                            {template.variables && template.variables.length > 0 && (
                              <div className="text-sm text-gray-500">
                                Available variables: {template.variables.map(v => `{{${v}}}`).join(', ')}
                              </div>
                            )}
                            
                            <Button 
                              size="sm" 
                              onClick={() => handleUpdateEmailTemplate(template.name, template.subject, template.body)}
                            >
                              Update Template
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      No email templates configured
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Security Settings */}
          <TabsContent value="security">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Security Configuration</CardTitle>
                  <CardDescription>
                    System security and access control settings
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-center py-8 text-gray-500">
                    Security settings will be implemented in future updates
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminSettings; 